import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";
import swal from "sweetalert2";
import {
  AccionImportacionBsc,
  BscArea,
  BscEjercicioKpi,
  BscImportacionFila,
  BscImportacionPedido,
  BscImportacionPlan,
  EstatusPlaneacionBsc,
  EstatusTrimestreBsc,
  ModoImportacionBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { BscService } from "../bsc.service";
import { ESTATUS_PLANEACION, ESTATUS_TRIMESTRE, EstadoBsc } from "../bsc-estados";

/** 1 subir, 2 revisar la vista previa, 3 resultado de la carga. */
type PasoCarga = 1 | 2 | 3;

/** Lo que se elige en pantalla. Nulo = "el que diga el libro". */
interface FormaCarga {
  modo: ModoImportacionBsc | null;
  anio: number | null;
  hastaTrimestre: number | null;
  idBscArea: number | null;
  hoja: string | null;
}

/** Un error o aviso del plan listo para mostrarse. El código va en el title, no en el texto. */
interface MensajeBsc {
  codigo: string;
  texto: string;
}

/** real · programado · peso de un trimestre hasta el corte, o "NA". */
interface CeldaTrimestre {
  q: number;
  texto: string;
  na: boolean;
  /** AVANCES: el trimestre está enviado o autorizado; la celda es lo guardado, no lo del libro. */
  conservado: boolean;
}

/**
 * Una fila del plan ya armada para la tabla. Se arma UNA vez por respuesta
 * del servidor (no en cada detección de cambios): arreglos nuevos en cada
 * pasada rehacen las filas y les quitan el foco.
 */
interface FilaVista {
  filaExcel: number;
  objetivo: string;
  indicador: string;
  meta: string;
  metaNota: string;
  trimestres: CeldaTrimestre[];
  pesosDespues: string;
  accion: AccionImportacionBsc;
  accionTexto: string;
  accionClase: string;
  /** "Maestro: «…»" cuando el objetivo del maestro no es el mismo texto. */
  maestro: string;
  nota: string;
  /** SUGERIDO sin confirmar: se ofrece "Es el mismo KPI". */
  sugerido: boolean;
  /** Confirmada o forzada por la pantalla ("fila:idKpi"): se ofrece deshacer. */
  asignada: boolean;
  idKpi: number | null;
  /** Nombres accesibles: empiezan con el texto visible (WCAG 2.5.3) y dicen de qué fila son. */
  etiquetaCasilla: string;
  etiquetaConfirmar: string;
  etiquetaDeshacer: string;
  avisos: MensajeBsc[];
  /** Un error del plan nombra esta fila. */
  conError: boolean;
  /**
   * Se puede marcar para cargar: no la dejó fuera el servidor (OMITIDA en
   * AVANCES: no es de la planeación autorizada) ni es un SUGERIDO de AVANCES
   * sin confirmar (no se carga hasta confirmarlo).
   */
  cargable: boolean;
}

/** Cómo queda un trimestre al confirmar. */
interface TrimestreVista {
  q: number;
  estado: EstadoBsc<EstatusTrimestreBsc>;
  /** AVANCES: qué pasa con su bloque del libro («se carga», «se conserva», «después del corte»). */
  nota: string;
}

/** «Enviar Qn a revisión» / «Cerrar Qn» del resultado: lleva a C3 con ese trimestre. */
interface CierreVista {
  q: number;
  texto: string;
  ruta: (string | number)[];
}

/** Lo que dejó la carga, contado sobre las filas que contestó cargar.do. */
interface ResumenCarga {
  cargados: number;
  creados: number;
  actualizados: number;
  iguales: number;
  omitidos: number;
}

/** BscLectorFor011.TOPE_BYTES: más grande, el servidor lo rechaza sin abrirlo. */
const TOPE_BYTES = 10 * 1024 * 1024;

/** Años hacia atrás que ofrece el combo (HISTORICO). */
const ANIOS_ATRAS = 10;

/** Años hacia adelante que ofrece el combo (PLANEACION del año siguiente). */
const ANIOS_ADELANTE = 1;

/** T3, a donde sigue una carga PLANEACION (con ?anio=&idBscArea=). */
const RUTA_PLANEACION = "/admin/bsc/planeacion";

/** C3 (mi-bsc/cierre/:idEjercicio/:trimestre), a donde lleva «Enviar Qn a revisión» del resultado. */
const RUTA_CIERRE = "/admin/bsc/mi-bsc/cierre";

/** Los trimestres que ya no reciben avances (la guarda del servidor). */
const TRIMESTRES_CERRADOS = new Set<EstatusTrimestreBsc>(["ENVIADO", "AUTORIZADO"]);

/**
 * La carga no coincide con la vista previa (huella, plan con errores, nada
 * marcado) o alguien cambió el año o el maestro en medio: se ofrece volver
 * a leer el archivo.
 */
const CODIGOS_REVISAR_DE_NUEVO = new Set(["VAL-BSC-IMPORTACION", "VAL-BSC-CONFLICTO"]);

/** Los modos de docs/BSC.md §6: PLANEACION (F5.3), AVANCES (F6.7), ARRANQUE e HISTORICO (3.4). */
const MODOS: { valor: ModoImportacionBsc; texto: string; ayuda: string; inhabilitado?: boolean }[] = [
  {
    valor: "PLANEACION",
    texto: "Planeación del año (metas)",
    ayuda:
      "Las metas del año en curso o del siguiente: la planeación queda en borrador (o se actualiza la que está en borrador o regresada) con metas, unidades, pesos y NA del libro; los avances no se cargan. Se revisa y se envía a autorización en Planeación.",
  },
  {
    valor: "AVANCES",
    texto: "Avances del año autorizado",
    ayuda:
      "El libro del año con su planeación ya autorizada: se cargan el real, el programado, los recursos y los textos de los meses hasta el corte, solo en los trimestres que no se han enviado ni autorizado. Metas, pesos y NA se quedan como se autorizaron. Después el trimestre se envía a revisión.",
  },
  {
    valor: "ARRANQUE",
    texto: "Arranque del año en curso",
    ayuda:
      "El año en curso ya avanzado: la planeación queda autorizada, los trimestres antes del corte autorizados y el del corte en captura. Exige los parámetros del año guardados.",
  },
  {
    valor: "HISTORICO",
    texto: "Histórico (años anteriores)",
    ayuda:
      "Un año anterior: los trimestres hasta el corte quedan autorizados y los demás pendientes. Si el año no existe se crea con los valores por omisión.",
  },
];

const ACCIONES: Record<AccionImportacionBsc, { texto: string; clase: string }> = {
  IGUAL: { texto: "KPI existente", clase: "is-inactive" },
  ACTUALIZAR: { texto: "Actualizar KPI existente", clase: "bsc-badge-info" },
  CREAR: { texto: "Crear KPI nuevo", clase: "is-active" },
  SUGERIDO: { texto: "¿Es un KPI existente?", clase: "is-pending" },
  OMITIDA: { texto: "No se carga", clase: "is-inactive" },
};

const PASOS: { numero: PasoCarga; texto: string }[] = [
  { numero: 1, texto: "Subir archivo" },
  { numero: 2, texto: "Revisar" },
  { numero: 3, texto: "Confirmar" },
];

/** `[Fila N: ]CODIGO[ Qn]: mensaje` (BscAviso.texto() y BscImportacionBuss). */
const FORMATO_MENSAJE = /^(?:(Fila \d+): )?([A-Z][A-Z0-9_-]+)(?: (Q[1-4]))?: ([\s\S]+)$/;

/** Las filas que nombra un error: "Fila 11:", "la fila 13", "Las filas 11 y 12". */
const FILAS_MENCIONADAS = /\b[Ff]ilas? (\d+)(?: y (\d+))?/g;

/** Lo que pasa con el bloque de cada trimestre en AVANCES ("Al confirmar" y el resultado). */
const NOTA_SE_CARGA = "se carga";
const NOTA_SE_CONSERVA = "se conserva";
const NOTA_DESPUES_DEL_CORTE = "después del corte";

const FORMATO_PCT = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 });
const FORMATO_NUM = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 });

/**
 * BSC · T1 Cargar Excel FOR-011 (docs/BSC.md §6, §7 y Anexo B).
 *
 * Tres pasos, calco de vacaciones/carga-excel-vacaciones: (1) se elige el
 * .xlsx y el modo; (2) previa.do lee el libro SIN ESCRIBIR y la pantalla
 * enseña lo que detectó (área, año, corte, versión, hoja) y el plan por KPI
 * contra el maestro del área; (3) cargar.do manda OTRA VEZ el mismo archivo
 * con la huella de la previa y las filas palomeadas, y el servidor lo vuelve
 * a leer y lo aplica todo o nada.
 *
 * Área, año, corte, modo, hoja y las asignaciones de KPI sugeridos entran en
 * la huella: cambiarlos vuelve a pedir la previa, y la carga manda los
 * MISMOS de la previa revisada (pedidoDelPlan). Las casillas de las filas no
 * entran: se mandan en `filas`.
 *
 * ARRANQUE e HISTORICO (llave admin/bsc/arranque) y PLANEACION (hoja
 * admin/bsc/planeacion): esta no tiene corte ("Cargar hasta" no se pide ni
 * se manda), la tabla enseña solo meta y pesos y el resultado lleva a
 * Planeación (T3), donde la planeación sigue en borrador. AVANCES (hoja
 * admin/bsc/mi-bsc, F6.7): las filas se cruzan con la planeación autorizada
 * (la que no es de ella llega OMITIDA y no se puede marcar), los trimestres
 * enviados o autorizados conservan lo guardado y "Al confirmar" dice qué
 * bloque se escribe. El resultado de AVANCES y de ARRANQUE lleva a C3 con
 * «Enviar Qn a revisión» (área con Excel) o «Cerrar Qn» cuando queda un
 * trimestre en captura o regresado. Lo que el servidor rechaza (sin la hoja
 * o la llave, sesión caducada) llega en codigoError con un texto de negocio;
 * ningún texto de excepción llega a la pantalla.
 *
 * Base visual: table-page de src/styles.css con los bloques de T4
 * (parametros-bsc), que es la referencia del módulo.
 */
@Component({
  selector: "app-carga-excel-bsc",
  standalone: true,
  templateUrl: "./carga-excel-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./carga-excel-bsc.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class CargaExcelBscComponent implements OnInit, OnDestroy {
  readonly modos = MODOS;
  readonly pasos = PASOS;
  readonly rutaPlaneacion = RUTA_PLANEACION;
  readonly trimestres = [1, 2, 3, 4];
  readonly topeMb = TOPE_BYTES / (1024 * 1024);
  /**
   * La opción "el del libro" de los combos de año y corte. mat-select pinta
   * vacío un valor nulo; con 0 se lee la opción, y 0 se vuelve nulo al
   * guardarlo en la forma (no hay año ni trimestre 0).
   */
  readonly delLibro = 0;

  paso: PasoCarga = 1;
  archivo: File | null = null;
  arrastrando = false;
  forma: FormaCarga = { modo: null, anio: null, hastaTrimestre: null, idBscArea: null, hoja: null };
  /** filaExcel → idKpi: los SUGERIDO confirmados (van en la huella). */
  asignaciones = new Map<number, number>();

  /** La previa a la vista y el pedido que la produjo (la carga manda ese). */
  plan: BscImportacionPlan | null = null;
  private pedidoDelPlan: BscImportacionPedido | null = null;
  private asignacionesDelPlan = new Map<number, number>();

  filas: FilaVista[] = [];
  errores: MensajeBsc[] = [];
  avisos: MensajeBsc[] = [];
  marcadas = new Set<number>();
  /** Trimestres con "real · prog · peso" (hasta el corte) y los que solo llevan peso. */
  columnasQ: number[] = [];
  columnasPeso: number[] = [];
  trimestresAlConfirmar: TrimestreVista[] = [];
  /** Las áreas del <select> cuando la celda "Área:" no cruzó (plan.areas). */
  areasParaElegir: BscArea[] = [];
  /**
   * Las hojas del <select> (plan.hojas: el servidor las manda solo con
   * VARIAS_HOJAS_CANDIDATAS u HOJA_NO_ENCONTRADA). Se conservan mientras haya
   * una hoja elegida a mano, como las áreas, para poder cambiarla.
   */
  hojasParaElegir: string[] = [];
  aniosOpciones: number[] = [];

  leyendo = false;
  cargando = false;
  /** Lo que impidió la primera previa (sin permiso, sin sesión, sin conexión). */
  errorPrevia = "";

  resultado: BscImportacionPlan | null = null;
  resumen: ResumenCarga | null = null;
  avisosResultado: MensajeBsc[] = [];
  trimestresResultado: TrimestreVista[] = [];

  /** catalogos.do: año en curso del servidor y nombres de las áreas. */
  private anioEnCurso = new Date().getFullYear();
  private areasCatalogo: BscArea[] = [];
  /** catalogos.do dijo que el perfil no tiene la hoja carga-excel. */
  sinHojaCarga = false;

  private catalogo?: Subscription;
  private previa?: Subscription;
  private carga?: Subscription;
  /** Solo cuenta la respuesta de la última previa pedida. */
  private folioPrevia = 0;
  /** Solo cuenta la copia del último archivo elegido. */
  private folioArchivo = 0;
  /**
   * A dónde va el foco al pintar (el primero que exista): el botón que lo
   * tenía se deshabilitó o *ngIf lo quitó, y sin esto cae al <body>.
   */
  private foco: string[] = [];
  /**
   * El foco al terminar la previa que se está leyendo (salga o falle): la
   * fila del SUGERIDO que se confirmó o deshizo, o el título de lo detectado
   * tras "Volver a leer". Con los select no hace falta: siguen ahí.
   */
  private focoTrasLeer: string[] = [];
  /**
   * SweetAlert vive en el <body> y sobrevive al cambio de ruta: al destruirse
   * la pantalla se cierra la pregunta que haya dejado abierta y sus
   * callbacks ya no hacen nada.
   */
  private preguntaAbierta = false;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.desdeEnlace();
    this.armaAnios();
    // Solo para el año en curso del servidor y los nombres de las áreas: si
    // falla, la carga sigue (la previa dice lo que haga falta).
    this.catalogo = this.bscService.catalogos().subscribe({
      next: (res) => {
        if (!res?.codigoError) {
          this.anioEnCurso = res.catalogos?.anioEnCurso ?? this.anioEnCurso;
          this.areasCatalogo = res.catalogos?.areas ?? [];
          this.sinHojaCarga = res.puedeCargarExcel === false;
          this.armaAnios();
          this.cdr.detectChanges();
        }
      },
      error: () => {
        // Sin catálogos la pantalla funciona con el año del navegador.
      },
    });
  }

  /**
   * ?modo=PLANEACION&anio=2027: el enlace «Cargar desde Excel» de Planeación
   * (T3) abre la carga ya en ese modo y con ese año (el año se puede volver a
   * «El del libro»). Un modo inhabilitado o que no existe se ignora.
   */
  private desdeEnlace(): void {
    const params = this.route.snapshot.queryParamMap;
    const modo = (params.get("modo") ?? "").toUpperCase();
    const elegido = MODOS.find((m) => m.valor === modo && !m.inhabilitado);
    if (elegido) {
      this.forma.modo = elegido.valor;
    }
    const anio = Number(params.get("anio"));
    if (Number.isInteger(anio) && anio > 1900 && anio < 3000) {
      this.forma.anio = anio;
    }
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.folioPrevia++;
    this.folioArchivo++;
    this.catalogo?.unsubscribe();
    this.previa?.unsubscribe();
    this.carga?.unsubscribe();
    if (this.preguntaAbierta && swal.isVisible()) {
      swal.close();
    }
  }

  /* ============================ Paso 1: el archivo ============================ */

  eligeArchivo(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files?.[0];
    // Se limpia SIEMPRE: sin esto, volver a elegir el mismo archivo después
    // de corregirlo en Excel no dispara el evento otra vez.
    input.value = "";
    if (archivo) {
      this.tomaArchivo(archivo);
    }
  }

  alArrastrar(ev: DragEvent): void {
    if (this.ocupado) {
      return;
    }
    ev.preventDefault();
    this.arrastrando = true;
  }

  alSoltar(ev: DragEvent): void {
    ev.preventDefault();
    this.arrastrando = false;
    const archivo = ev.dataTransfer?.files?.[0];
    if (archivo && !this.ocupado) {
      this.tomaArchivo(archivo);
    }
  }

  /**
   * Las mismas dos reglas que el servidor (BscLectorFor011): solo .xlsx y a
   * lo más 10 MB. Se guarda una copia en memoria de los bytes: la previa y
   * la carga mandan SIEMPRE lo que se revisó. Un File del disco que se edita
   * en Excel después de elegirlo ya no se sube (Chrome y Edge cortan con
   * ERR_UPLOAD_FILE_CHANGED, que llega como "sin conexión"); el Excel
   * corregido se vuelve a elegir con "Cambiar archivo".
   */
  private tomaArchivo(archivo: File): void {
    if (!/\.xlsx$/i.test(archivo.name)) {
      this.msg.warningCall("El archivo tiene que ser un libro de Excel .xlsx (el FOR-011).", "Archivo no válido", null);
      return;
    }
    if (archivo.size > TOPE_BYTES) {
      this.msg.warningCall(`El archivo pasa de ${this.topeMb} MB.`, "Archivo muy grande", null);
      return;
    }
    const folio = ++this.folioArchivo;
    archivo.arrayBuffer().then(
      (bytes) => {
        if (folio === this.folioArchivo && !this.destruido) {
          this.usaArchivo(new File([bytes], archivo.name, { type: archivo.type, lastModified: archivo.lastModified }));
        }
      },
      () => {
        if (folio === this.folioArchivo && !this.destruido) {
          this.msg.warningCall("No se pudo leer el archivo en este equipo. Vuelve a elegirlo.", "Archivo no válido", null);
        }
      },
    );
  }

  /**
   * Un archivo nuevo empieza de cero: su área, hoja y KPIs son otros. En el
   * paso 1 se respetan el año y el corte ya elegidos; en la revisión eran
   * los del libro anterior y se vuelve a leer en seguida.
   */
  private usaArchivo(archivo: File): void {
    const enRevision = this.paso === 2;
    this.olvidaPlan();
    this.archivo = archivo;
    this.errorPrevia = "";
    this.forma = enRevision
      ? { ...this.forma, anio: null, hastaTrimestre: null, idBscArea: null, hoja: null }
      : { ...this.forma, idBscArea: null, hoja: null };
    this.paso = 1;
    this.cdr.detectChanges();
    if (enRevision && this.forma.modo) {
      this.pidePrevia();
    }
  }

  get puedeRevisar(): boolean {
    return !!this.archivo && !!this.forma.modo && !this.leyendo;
  }

  get ayudaModo(): string {
    return MODOS.find((m) => m.valor === this.forma.modo)?.ayuda ?? "Elige cómo entra el libro.";
  }

  /** PLANEACION no carga avances: sin "Cargar hasta" ni columnas de real y programado. */
  get esPlaneacion(): boolean {
    return this.forma.modo === "PLANEACION";
  }

  /**
   * AVANCES (y cualquier modo): la fila se puede marcar. OMITIDA la dejó
   * fuera el servidor; un SUGERIDO de AVANCES solo se carga si se confirma.
   */
  private esCargable(modo: ModoImportacionBsc | undefined, accion: AccionImportacionBsc | undefined): boolean {
    return accion !== "OMITIDA" && !(modo === "AVANCES" && accion === "SUGERIDO");
  }

  revisar(): void {
    if (this.puedeRevisar) {
      this.pidePrevia();
    }
  }

  /* ============================ Paso 2: la previa ============================ */

  /**
   * Pide la vista previa con lo que dicen los controles. Si ya hay una a la
   * vista, se queda mientras llega la nueva; si la nueva falla, los
   * controles regresan a lo de la que se ve.
   */
  private pidePrevia(): void {
    const archivo = this.archivo;
    const modo = this.forma.modo;
    if (!archivo || !modo) {
      return;
    }
    const pedido: BscImportacionPedido = {
      modo,
      anio: this.forma.anio,
      hoja: this.forma.hoja,
      idBscArea: this.forma.idBscArea,
      // PLANEACION no tiene corte: el servidor lo ignora y no se manda.
      hastaTrimestre: modo === "PLANEACION" ? null : this.forma.hastaTrimestre,
      asignaciones: this.textoAsignaciones(this.asignaciones),
    };
    const asignaciones = new Map(this.asignaciones);
    const folio = ++this.folioPrevia;
    this.previa?.unsubscribe();
    this.leyendo = true;
    this.errorPrevia = "";
    this.cdr.detectChanges();

    this.previa = this.bscService.importarPrevia(archivo, pedido).subscribe({
      next: (plan) => {
        if (folio !== this.folioPrevia) {
          return;
        }
        this.leyendo = false;
        if (!plan || plan.codigoError) {
          this.previaFallida(this.mensaje(plan, "No se pudo leer el archivo."));
          return;
        }
        this.aplicaPlan(plan, pedido, asignaciones);
        this.pinta();
      },
      error: () => {
        if (folio !== this.folioPrevia) {
          return;
        }
        this.leyendo = false;
        this.previaFallida("No se pudo leer el archivo: sin conexión con el servidor.");
      },
    });
  }

  /**
   * Sin previa a la vista, el error se queda en el paso 1; con previa, regresa
   * los controles. Se pinta (y se mueve el foco) antes del aviso: SweetAlert
   * devuelve el foco al cerrar a donde estaba al abrir.
   */
  private previaFallida(texto: string): void {
    if (this.plan && this.pedidoDelPlan) {
      this.formaDe(this.pedidoDelPlan);
      this.asignaciones = new Map(this.asignacionesDelPlan);
      this.foco = this.focoTrasLeer;
      this.focoTrasLeer = [];
      this.pinta();
      this.msg.warningCall(texto, "No se pudo volver a leer el archivo", null);
      return;
    }
    this.errorPrevia = texto;
    this.paso = 1;
    this.foco = ["#carga-bsc-subir"];
    this.pinta();
  }

  private aplicaPlan(plan: BscImportacionPlan, pedido: BscImportacionPedido, asignaciones: Map<number, number>): void {
    const anterior = this.plan;
    this.plan = plan;
    this.pedidoDelPlan = pedido;
    this.formaDe(pedido);
    // Una asignación de una fila que el libro ya no trae no se arrastra.
    const enElLibro = new Set((plan.filas ?? []).map((f) => f.filaExcel));
    asignaciones.forEach((_, fila) => {
      if (!enElLibro.has(fila)) {
        asignaciones.delete(fila);
      }
    });
    this.asignaciones = asignaciones;
    this.asignacionesDelPlan = new Map(asignaciones);
    // Las áreas y las hojas de los select: las que manda la previa cuando la
    // celda no cruzó o la hoja no salió sola; se conservan mientras haya una
    // elegida a mano, para poder cambiarla.
    if (plan.areas && plan.areas.length > 0) {
      this.areasParaElegir = plan.areas;
    } else if (pedido.idBscArea == null) {
      this.areasParaElegir = [];
    }
    if (plan.hojas && plan.hojas.length > 0) {
      this.hojasParaElegir = plan.hojas;
    } else if (pedido.hoja == null) {
      this.hojasParaElegir = [];
    }
    if (plan.anio != null && !this.aniosOpciones.includes(plan.anio)) {
      this.aniosOpciones = [...this.aniosOpciones, plan.anio].sort((a, b) => b - a);
    }
    this.preparaSeleccion(plan, anterior);
    this.armaVista(plan);
    // Del paso 1 al 2 el foco va al título de lo detectado.
    this.foco = this.paso !== 2 ? ["#carga-bsc-detectado"] : this.focoTrasLeer;
    this.focoTrasLeer = [];
    this.paso = 2;
  }

  /**
   * Lo palomeado se respeta entre previas; una fila nueva entra marcada. Una
   * que no se puede cargar (OMITIDA, SUGERIDO de AVANCES sin confirmar) no se
   * marca; si deja de estarlo (se confirmó el SUGERIDO) entra marcada.
   */
  private preparaSeleccion(plan: BscImportacionPlan, anterior: BscImportacionPlan | null): void {
    const antes = new Map((anterior?.filas ?? []).map((f) => [f.filaExcel, this.esCargable(anterior?.modo, f.accion)]));
    const marcadas = new Set<number>();
    for (const f of plan.filas ?? []) {
      if (f.filaExcel == null || !this.esCargable(plan.modo, f.accion)) {
        continue;
      }
      if (antes.get(f.filaExcel) !== true || this.marcadas.has(f.filaExcel)) {
        marcadas.add(f.filaExcel);
      }
    }
    this.marcadas = marcadas;
  }

  /* ---------- Lo que cambia la huella: se vuelve a leer ---------- */

  cambiaModo(modo: ModoImportacionBsc): void {
    if (modo !== this.forma.modo) {
      this.forma.modo = modo;
      this.releer();
    }
  }

  /** Los idKpi confirmados son del maestro del área anterior: no se arrastran. */
  cambiaArea(idBscArea: number | null): void {
    if (idBscArea !== this.forma.idBscArea) {
      this.forma.idBscArea = idBscArea;
      this.asignaciones = new Map();
      this.releer();
    }
  }

  cambiaAnio(anio: number | null): void {
    if (anio !== this.forma.anio) {
      this.forma.anio = anio;
      this.releer();
    }
  }

  cambiaHoja(hoja: string | null): void {
    if (hoja !== this.forma.hoja) {
      this.forma.hoja = hoja;
      this.releer();
    }
  }

  /**
   * Como el año: el combo enseña lo elegido ("el que detecte el libro" con el
   * Q aplicado en su texto), así lo que se ve y lo que se pide son lo mismo
   * aunque una previa falle y los controles regresen.
   */
  cambiaCorte(q: number | null): void {
    if (q !== this.forma.hastaTrimestre) {
      this.forma.hastaTrimestre = q;
      this.releer();
    }
  }

  /** El SUGERIDO es el mismo KPI del maestro: va como asignación "fila:idKpi". */
  confirmaSugerido(v: FilaVista): void {
    if (v.idKpi == null || this.ocupado) {
      return;
    }
    this.asignaciones.set(v.filaExcel, v.idKpi);
    this.releer(this.focoDeLaFila(v.filaExcel));
  }

  quitaAsignacion(v: FilaVista): void {
    if (!this.asignaciones.has(v.filaExcel) || this.ocupado) {
      return;
    }
    this.asignaciones.delete(v.filaExcel);
    this.releer(this.focoDeLaFila(v.filaExcel));
  }

  private releer(foco: string[] = []): void {
    if (this.paso === 2) {
      this.focoTrasLeer = foco;
      this.pidePrevia();
    }
  }

  /* ---------- Selección de filas (no entra en la huella) ---------- */

  marcaFila(fila: number, marcada: boolean): void {
    if (marcada) {
      this.marcadas.add(fila);
      return;
    }
    this.marcadas.delete(fila);
  }

  marcaTodas(marcadas: boolean): void {
    this.marcadas = new Set(marcadas ? this.filas.filter((f) => f.cargable).map((f) => f.filaExcel) : []);
  }

  get todasMarcadas(): boolean {
    const cargables = this.filas.filter((f) => f.cargable);
    return cargables.length > 0 && cargables.every((f) => this.marcadas.has(f.filaExcel));
  }

  get algunaMarcada(): boolean {
    return this.filas.some((f) => f.cargable && this.marcadas.has(f.filaExcel));
  }

  get nMarcadas(): number {
    return this.filas.filter((f) => f.cargable && this.marcadas.has(f.filaExcel)).length;
  }

  /** Ninguna fila se puede marcar (todas fuera de la planeación, por ejemplo): el «marcar todas» no sirve. */
  get ningunaCargable(): boolean {
    return !this.filas.some((f) => f.cargable);
  }

  /* ============================ Paso 3: la carga ============================ */

  get ocupado(): boolean {
    return this.leyendo || this.cargando;
  }

  get puedeConfirmar(): boolean {
    return (
      !!this.plan &&
      this.plan.valido === true &&
      !!this.plan.huella &&
      !!this.archivo &&
      !!this.pedidoDelPlan &&
      !this.ocupado &&
      this.nMarcadas > 0
    );
  }

  get textoConfirmar(): string {
    if (this.cargando) {
      return "Cargando…";
    }
    if (this.plan && this.plan.valido !== true) {
      // No todo se corrige en el Excel: el área, el modo o los Parámetros.
      return this.eligeArea && this.forma.idBscArea == null
        ? "Elige el área para confirmar"
        : "Resuelve lo que impide confirmar";
    }
    const n = this.nMarcadas;
    if (n === 0) {
      return "Marca al menos un KPI";
    }
    return `Confirmar carga · ${n} ${n === 1 ? "KPI" : "KPIs"}`;
  }

  confirmar(): void {
    const plan = this.plan;
    if (!this.puedeConfirmar || !plan) {
      return;
    }
    const n = this.nMarcadas;
    const total = this.filas.length;
    if (plan.modo === "PLANEACION") {
      const destino =
        plan.idEjercicio == null
          ? `la planeación ${plan.anio ?? ""} de ${this.nombreArea(plan)}, que se abre en borrador`
          : `la planeación ${plan.anio ?? ""} de ${this.nombreArea(plan)} (${this.textoEstatus(plan.estatusEjercicio).toLowerCase()})`;
      this.pregunta(
        `Se cargarán ${n} de ${total} KPI del FOR-011 a ${destino}: metas, unidades, pesos y NA. ` +
          "No se autoriza nada: la planeación se revisa y se envía a autorización en Planeación.",
        () => this.cargar(),
      );
      return;
    }
    if (plan.modo === "AVANCES") {
      const seCargan = this.trimestresAlConfirmar.filter((t) => t.nota === NOTA_SE_CARGA).map((t) => `Q${t.q}`);
      const seConservan = this.trimestresAlConfirmar.filter((t) => t.nota === NOTA_SE_CONSERVA).map((t) => `Q${t.q}`);
      const comoEstan = seConservan.length === 1 ? "se conserva como está" : "se conservan como están";
      this.pregunta(
        `Se cargarán los avances de ${this.lista(seCargan)} de ${n} de ${total} KPI del FOR-011 a la planeación ` +
          `${plan.anio ?? ""} autorizada de ${this.nombreArea(plan)}` +
          (seConservan.length > 0
            ? `; ${this.lista(seConservan)} ${comoEstan}`
            : "") +
          ". Metas y pesos no cambian y ningún trimestre se envía: el envío a revisión se hace después.",
        () => this.cargar(),
      );
      return;
    }
    const trimestres = this.trimestresAlConfirmar.map((t) => `Q${t.q} ${t.estado.texto.toLowerCase()}`).join(", ");
    const texto =
      `Se cargarán ${n} de ${total} KPI del FOR-011 a ${this.nombreArea(plan)} ${plan.anio ?? ""}` +
      ` (${this.textoModo(plan.modo).toLowerCase()}, hasta Q${plan.hastaTrimestre}). ` +
      `La planeación queda autorizada y los trimestres así: ${trimestres}.`;
    this.pregunta(texto, () => this.cargar());
  }

  private cargar(): void {
    const plan = this.plan;
    const archivo = this.archivo;
    const pedido = this.pedidoDelPlan;
    if (!plan || !archivo || !pedido || !this.puedeConfirmar) {
      return;
    }
    const filas = this.filas.map((f) => f.filaExcel).filter((f) => this.marcadas.has(f));
    this.carga?.unsubscribe();
    this.cargando = true;
    this.cdr.detectChanges();

    this.carga = this.bscService.importarCargar(archivo, pedido, plan.huella ?? "", filas).subscribe({
      next: (res) => {
        this.cargando = false;
        if (!res || res.codigoError) {
          this.foco = ["#carga-bsc-confirmar"];
          this.pinta();
          this.cargaFallida(res);
          return;
        }
        this.muestraResultado(res);
        this.pinta();
      },
      error: () => {
        this.cargando = false;
        this.foco = ["#carga-bsc-confirmar"];
        this.pinta();
        this.msg.errorCall(
          "No se pudo cargar el archivo: sin conexión con el servidor. Si la conexión se cortó a media carga, " +
            "vuelve a revisar el archivo: la vista previa dice si ya quedó cargado.",
          null,
          null,
        );
      },
    });
  }

  /**
   * La huella ya no coincide, el plan cambió o alguien movió el año o el
   * maestro en medio: no se guardó nada y se ofrece volver a leer. Lo demás
   * (sin permiso sobre el área, sesión caducada) solo se dice.
   */
  private cargaFallida(res: BscImportacionPlan | null | undefined): void {
    const texto = this.mensaje(res, "No se pudo cargar el archivo. No se guardó nada.");
    if (res && CODIGOS_REVISAR_DE_NUEVO.has(res.codigoError ?? "")) {
      this.pregunta(`${texto} ¿Volver a leer el archivo para revisar la vista previa?`, () => {
        this.focoTrasLeer = ["#carga-bsc-detectado"];
        this.pidePrevia();
      });
      return;
    }
    this.msg.warningCall(texto, "No se cargó el archivo", null);
  }

  private muestraResultado(res: BscImportacionPlan): void {
    const filas = res.filas ?? [];
    const cuenta = (accion: AccionImportacionBsc) => filas.filter((f) => f.accion === accion).length;
    const omitidos = cuenta("OMITIDA");
    // Un SUGERIDO que no se confirmó se dio de alta como KPI nuevo.
    this.resumen = {
      cargados: filas.length - omitidos,
      creados: cuenta("CREAR") + cuenta("SUGERIDO"),
      actualizados: cuenta("ACTUALIZAR"),
      iguales: cuenta("IGUAL"),
      omitidos,
    };
    if (res.modo === "AVANCES") {
      // AVANCES no crea ni actualiza KPIs del maestro: lo cargado es IGUAL.
      this.resumen = { cargados: filas.length - omitidos, creados: 0, actualizados: 0, iguales: cuenta("IGUAL"), omitidos };
    }
    this.resultado = res;
    this.avisosResultado = (res.avisos ?? []).map((a) => this.parte(a));
    // En el resultado, lo que ya pasó con cada bloque.
    this.trimestresResultado = this.trimestresDe(res).map((t) => ({
      ...t,
      nota: notaDelResultado(t.nota),
    }));
    this.foco = ["#carga-bsc-resultado"];
    this.paso = 3;
  }

  /** Cancelar la revisión o empezar otra carga: no queda nada a medias en el servidor. */
  reiniciar(): void {
    this.folioPrevia++;
    this.folioArchivo++;
    this.previa?.unsubscribe();
    this.olvidaPlan();
    this.archivo = null;
    this.resultado = null;
    this.resumen = null;
    this.avisosResultado = [];
    this.trimestresResultado = [];
    this.errorPrevia = "";
    this.leyendo = false;
    this.forma = { modo: this.forma.modo, anio: null, hastaTrimestre: null, idBscArea: null, hoja: null };
    this.paso = 1;
    this.foco = ["#carga-bsc-subir"];
    this.pinta();
  }

  private olvidaPlan(): void {
    this.plan = null;
    this.pedidoDelPlan = null;
    this.asignaciones = new Map();
    this.asignacionesDelPlan = new Map();
    this.filas = [];
    this.errores = [];
    this.avisos = [];
    this.marcadas = new Set();
    this.columnasQ = [];
    this.columnasPeso = [];
    this.trimestresAlConfirmar = [];
    this.areasParaElegir = [];
    this.hojasParaElegir = [];
  }

  /* ============================ La vista del plan ============================ */

  /** Todo lo que pinta la tabla y el encabezado, armado una vez por respuesta. */
  private armaVista(plan: BscImportacionPlan): void {
    // PLANEACION: ningún trimestre "hasta el corte"; solo los pesos de los cuatro.
    const corte = plan.modo === "PLANEACION" ? 0 : (plan.hastaTrimestre ?? 4);
    this.columnasQ = this.trimestres.filter((q) => q <= corte);
    this.columnasPeso = this.trimestres.filter((q) => q > corte);
    this.errores = (plan.errores ?? []).map((e) => this.parte(e));
    this.avisos = (plan.avisos ?? []).map((a) => this.parte(a));
    const conError = new Set<number>();
    for (const e of plan.errores ?? []) {
      for (const m of e.matchAll(FILAS_MENCIONADAS)) {
        conError.add(Number(m[1]));
        if (m[2]) {
          conError.add(Number(m[2]));
        }
      }
    }
    this.filas = (plan.filas ?? [])
      .filter((f) => f.filaExcel != null)
      .map((f) => this.armaFila(f, corte, conError.has(f.filaExcel as number)));
    this.trimestresAlConfirmar = this.trimestresDe(plan);
  }

  private armaFila(f: BscImportacionFila, corte: number, conError: boolean): FilaVista {
    const k: BscEjercicioKpi = f.kpi ?? {};
    const accion: AccionImportacionBsc = f.accion ?? "CREAR";
    const filaExcel = f.filaExcel as number;
    const objetivo = (k.objetivo ?? "").trim();
    const maestro = (f.objetivoMaestro ?? "").trim();
    const asignada = this.asignaciones.has(filaExcel);
    // "(confirmado)" solo si el servidor la aplicó: una asignación que
    // rechazó (ASIGNACION_INVALIDA) se queda con su error y "Deshacer".
    const aplicada =
      asignada && f.idKpi === this.asignaciones.get(filaExcel) && (accion === "IGUAL" || accion === "ACTUALIZAR");
    const mismoTexto = maestro.toLowerCase() === objetivo.toLowerCase();
    // AVANCES cruza con los KPI de la planeación autorizada, no con el maestro.
    const deDonde = this.plan?.modo === "AVANCES" ? "de la planeación autorizada" : "del maestro";
    let textoMaestro = "";
    if (maestro && !mismoTexto) {
      textoMaestro = this.textoDelMaestro(maestro, accion, f.similitud);
    }
    const { meta, nota } = this.textoMeta(f, k);
    const textoObjetivo = objetivo || "(sin objetivo)";
    const cargable = this.esCargable(this.plan?.modo, accion);
    return {
      filaExcel,
      objetivo: textoObjetivo,
      indicador: (k.indicador ?? "").trim(),
      meta,
      metaNota: nota,
      trimestres: this.trimestres
        .filter((q) => q <= corte)
        .map((q) => this.celdaTrimestre(f, k, q)),
      pesosDespues: this.trimestres
        .filter((q) => q > corte)
        .map((q) => (this.aplica(k, q) ? this.pct(this.peso(k, q)) : "NA"))
        .join(" · "),
      accion,
      accionTexto: aplicada ? `${ACCIONES[accion].texto} (confirmado)` : ACCIONES[accion].texto,
      accionClase: ACCIONES[accion].clase,
      maestro: textoMaestro,
      nota: (f.nota ?? "").trim(),
      sugerido: accion === "SUGERIDO" && f.idKpi != null && !asignada,
      asignada,
      idKpi: f.idKpi ?? null,
      etiquetaCasilla: cargable
        ? `Cargar la fila ${filaExcel}: ${textoObjetivo}`
        : `La fila ${filaExcel} no se carga: ${textoObjetivo}`,
      etiquetaConfirmar: maestro
        ? `Es el mismo KPI: la fila ${filaExcel} es «${maestro}» ${deDonde}`
        : `Es el mismo KPI: la fila ${filaExcel}`,
      etiquetaDeshacer: `Deshacer la confirmación de la fila ${filaExcel}`,
      // KPI_SUGERIDO ya lo dice la columna de acción, con el botón para confirmarlo.
      avisos: (f.avisos ?? []).map((a) => this.parte(a)).filter((a) => a.codigo !== "KPI_SUGERIDO"),
      conError,
      cargable,
    };
  }

  /** Con qué KPI del maestro (o de la planeación en AVANCES) se emparejó la fila, cuando su texto es otro. */
  private textoDelMaestro(maestro: string, accion: AccionImportacionBsc, similitud: number | undefined): string {
    if (accion === "SUGERIDO" && similitud != null) {
      return `Se parece a «${maestro}» (${Math.round(Number(similitud) * 100)} % de palabras en común).`;
    }
    return this.plan?.modo === "AVANCES" ? `En la planeación: «${maestro}».` : `Maestro: «${maestro}».`;
  }

  /** La meta como quedaría (BscMetaParser): fracción en % o número con su unidad. */
  private textoMeta(f: BscImportacionFila, k: BscEjercicioKpi): { meta: string; nota: string } {
    const celda = (f.metaCelda ?? "").trim();
    if (k.metaValor == null) {
      return { meta: celda || "—", nota: celda ? "Sin valor numérico" : "" };
    }
    const unidad = k.unidad ?? "%";
    const textoUnidad = unidad === "dias" ? "días" : unidad;
    const meta =
      unidad === "%"
        ? `${this.pct(k.metaValor)} %`
        : `${FORMATO_NUM.format(Number(k.metaValor))} ${textoUnidad}`;
    // Una meta escrita como texto ("6 productos") se enseña tal cual vino.
    const nota = k.metaTexto?.trim() ? `Celda: «${k.metaTexto.trim()}»` : "";
    return { meta, nota };
  }

  private celdaTrimestre(f: BscImportacionFila, k: BscEjercicioKpi, q: number): CeldaTrimestre {
    // AVANCES: un trimestre enviado o autorizado enseña lo guardado (el servidor lo manda así).
    const conservado =
      this.plan?.modo === "AVANCES" && f.accion === "IGUAL" && TRIMESTRES_CERRADOS.has(this.estatusDe(this.plan, q));
    if (!this.aplica(k, q)) {
      return { q, texto: "NA", na: true, conservado };
    }
    const t = (f.trimestres ?? []).find((x) => x.trimestre === q);
    return {
      q,
      texto: `${this.pct(t?.avanceReal)} · ${this.pct(t?.avanceProgramado)} · ${this.pct(this.peso(k, q))}`,
      na: false,
      conservado,
    };
  }

  private aplica(k: BscEjercicioKpi, q: number): boolean {
    return [k.aplicaQ1, k.aplicaQ2, k.aplicaQ3, k.aplicaQ4][q - 1] !== false;
  }

  private peso(k: BscEjercicioKpi, q: number): number | undefined {
    return [k.pesoQ1, k.pesoQ2, k.pesoQ3, k.pesoQ4][q - 1];
  }

  /**
   * Cómo quedan los 4 trimestres al cargar: los de plan.estatusTrimestres
   * (desde F6.7) o, si no vienen, la regla de BscImportacionBuss.estatusTrimestres
   * (ARRANQUE Q1..Q(n-1) AUTORIZADO, Qn EN_CAPTURA, resto PENDIENTE;
   * HISTORICO Q1..Qn AUTORIZADO, resto PENDIENTE). En AVANCES son los de la
   * planeación, que la carga no cambia, con lo que pasa con cada bloque.
   */
  private trimestresDe(plan: BscImportacionPlan): TrimestreVista[] {
    const hasta = plan.hastaTrimestre;
    if (!hasta || plan.modo === "PLANEACION") {
      return [];
    }
    if (plan.modo === "AVANCES" && (plan.estatusTrimestres ?? []).length !== 4) {
      return [];
    }
    return this.trimestres.map((q) => {
      const estatus = this.estatusDe(plan, q);
      const estado = ESTATUS_TRIMESTRE.find((e) => e.valor === estatus) ?? ESTATUS_TRIMESTRE[0];
      let nota = "";
      if (plan.modo === "AVANCES") {
        nota = notaDeAvances(q, hasta, estatus);
      }
      return { q, estado, nota };
    });
  }

  /** El estatus de Qq: el que manda el servidor o el que deja la carga según el modo y el corte. */
  private estatusDe(plan: BscImportacionPlan | null, q: number): EstatusTrimestreBsc {
    const delServidor = plan?.estatusTrimestres ?? [];
    if (delServidor.length === 4) {
      return delServidor[q - 1];
    }
    const hasta = plan?.hastaTrimestre ?? 0;
    if (q < hasta || (q === hasta && plan?.modo === "HISTORICO")) {
      return "AUTORIZADO";
    }
    return q === hasta ? "EN_CAPTURA" : "PENDIENTE";
  }

  /* ============================ Presentación ============================ */

  get detectadoKpis(): string {
    const n = this.filas.length;
    if (n === 0) {
      return "Ninguno";
    }
    const primera = this.filas[0].filaExcel;
    const ultima = this.filas[n - 1].filaExcel;
    return n === 1 ? `1 · fila ${primera}` : `${n} · filas ${primera} a ${ultima}`;
  }

  /** "Q1 y Q2 · Q3 y Q4 solo plan"; en AVANCES "Se carga Q2 · Q1 se conserva · Q3 y Q4 solo plan". */
  get detectadoConDatos(): string {
    if (this.plan?.modo === "PLANEACION") {
      return "No se cargan: solo metas y pesos";
    }
    const n = this.plan?.hastaTrimestre;
    if (!n) {
      return "Sin corte";
    }
    if (this.plan?.modo === "AVANCES" && this.trimestresAlConfirmar.length === 4) {
      return this.conDatosDeAvances(n);
    }
    const con = this.rango(1, n);
    return n === 4 ? con : `${con} · ${this.rango(n + 1, 4)} solo plan`;
  }

  /** detectadoConDatos en AVANCES: "Se carga Q2 · Q1 se conserva · Q3 y Q4 solo plan" (n = el corte). */
  private conDatosDeAvances(n: number): string {
    const cargan = this.trimestresAlConfirmar.filter((t) => t.nota === NOTA_SE_CARGA).map((t) => `Q${t.q}`);
    const conservan = this.trimestresAlConfirmar.filter((t) => t.nota === NOTA_SE_CONSERVA).map((t) => `Q${t.q}`);
    const seCargan = cargan.length === 1 ? "carga" : "cargan";
    const partes = [cargan.length > 0 ? `Se ${seCargan} ${this.lista(cargan)}` : "Ninguno se carga"];
    if (conservan.length > 0) {
      partes.push(`${this.lista(conservan)} ${conservan.length === 1 ? "se conserva" : "se conservan"}`);
    }
    if (n < 4) {
      partes.push(`${this.rango(n + 1, 4)} solo plan`);
    }
    return partes.join(" · ");
  }

  get detectadoCorte(): string {
    const p = this.plan;
    if (!p?.corteExcel) {
      return "vacía";
    }
    return p.trimestreCorte ? `«${p.corteExcel}» → Q${p.trimestreCorte}` : `«${p.corteExcel}»`;
  }

  get detectadoArchivo(): string {
    const partes: string[] = [];
    if (this.archivo) {
      partes.push(this.tamano(this.archivo.size));
    }
    if (this.plan?.versionFormato) {
      partes.push(`formato v${this.plan.versionFormato}`);
    }
    if (this.plan?.hoja) {
      partes.push(`hoja «${this.plan.hoja}»`);
    }
    return partes.join(" · ");
  }

  /**
   * PLANEACION: en qué queda la planeación al confirmar (nueva en borrador o
   * la que ya existe, con su estatus) y contra qué año se comparan las metas.
   * Nulo en los demás modos o si la planeación no se puede tocar (ese error
   * ya lo dice la lista de lo que impide confirmar).
   */
  get planeacionAlConfirmar(): { estado: EstadoBsc<EstatusPlaneacionBsc>; texto: string } | null {
    const p = this.plan;
    if (p?.modo !== "PLANEACION") {
      return null;
    }
    const estatus: EstatusPlaneacionBsc = (p.estatusEjercicio as EstatusPlaneacionBsc | undefined) ?? "BORRADOR";
    if (estatus !== "BORRADOR" && estatus !== "REGRESADA") {
      return null;
    }
    const comparada = p.anioOrigen ? `metas comparadas con ${p.anioOrigen}` : "sin año con qué comparar las metas";
    const sigue = estatus === "REGRESADA" ? "regresada" : "en borrador";
    return {
      estado: this.estadoPlaneacion(estatus),
      texto:
        p.idEjercicio == null
          ? `Se abre la planeación ${p.anio ?? ""} en borrador; ${comparada}.`
          : `Se actualiza la planeación ${p.anio ?? ""}, que sigue ${sigue}; ${comparada}.`,
    };
  }

  /** El resultado de una carga PLANEACION: el estatus en que quedó la planeación. */
  get estadoResultado(): EstadoBsc<EstatusPlaneacionBsc> {
    return this.estadoPlaneacion((this.resultado?.estatusEjercicio as EstatusPlaneacionBsc | undefined) ?? "BORRADOR");
  }

  /** ?anio=&idBscArea= para que Planeación (T3) abra el área y el año cargados. */
  get queryPlaneacion(): Record<string, number | null> {
    return { anio: this.resultado?.anio ?? null, idBscArea: this.resultado?.idBscArea ?? null };
  }

  /** La celda "Área:" no cruzó o se eligió a mano: se enseña el <select>. */
  get eligeArea(): boolean {
    return this.areasParaElegir.length > 0;
  }

  get textoResultado(): string {
    const r = this.resultado;
    if (!r) {
      return "";
    }
    return (
      `${this.nombreArea(r)} · ${r.anio ?? ""} · ${this.textoModo(r.modo)}` +
      (r.hastaTrimestre ? ` hasta Q${r.hastaTrimestre}` : "")
    );
  }

  /** Lo que queda abierto después de un ARRANQUE: el trimestre del corte, en captura. */
  get trimestreEnCaptura(): number | null {
    return this.trimestresResultado.find((t) => t.estado.valor === "EN_CAPTURA")?.q ?? null;
  }

  /**
   * «Enviar Qn a revisión» (área con Excel) o «Cerrar Qn» después de cargar
   * en AVANCES o ARRANQUE: el primer trimestre en captura o regresado, si
   * quien cargó puede cerrar (puedeCerrar). Lleva a C3, que vuelve a revisar
   * cada punto (meses, real, fechas, el trimestre anterior autorizado) antes de
   * enviar con cerrar.do: las dos vías terminan en el mismo cierre (§1.1).
   */
  get cierreResultado(): CierreVista | null {
    const r = this.resultado;
    if (!r || (r.modo !== "AVANCES" && r.modo !== "ARRANQUE") || r.puedeCerrar !== true || r.idEjercicio == null) {
      return null;
    }
    const q = this.trimestresResultado.find((t) => t.estado.valor === "EN_CAPTURA" || t.estado.valor === "REGRESADO")?.q;
    if (q == null) {
      return null;
    }
    return {
      q,
      texto: r.viaCaptura === "EXCEL" ? `Enviar Q${q} a revisión` : `Cerrar Q${q}`,
      ruta: [RUTA_CIERRE, r.idEjercicio, q],
    };
  }

  /**
   * El nombre que manda el plan (también el de un área INACTIVA, que no está
   * en catalogos.do); si no, el del catálogo o el de la celda, nunca un id.
   */
  nombreArea(p: BscImportacionPlan | null | undefined): string {
    if (p?.nombreArea) {
      return p.nombreArea;
    }
    const id = p?.idBscArea;
    const area =
      id == null
        ? undefined
        : this.areasCatalogo.find((a) => a.idBscArea === id) ?? this.areasParaElegir.find((a) => a.idBscArea === id);
    if (area?.nombre) {
      return area.nombre;
    }
    return p?.areaExcel ? `«${p.areaExcel}»` : "el área";
  }

  textoModo(modo: ModoImportacionBsc | null | undefined): string {
    return MODOS.find((m) => m.valor === modo)?.texto ?? (modo ?? "");
  }

  private estadoPlaneacion(estatus: EstatusPlaneacionBsc): EstadoBsc<EstatusPlaneacionBsc> {
    return ESTATUS_PLANEACION.find((e) => e.valor === estatus) ?? ESTATUS_PLANEACION[0];
  }

  private textoEstatus(estatus: string | null | undefined): string {
    return ESTATUS_PLANEACION.find((e) => e.valor === estatus)?.texto ?? "Borrador";
  }

  esSugerida(area: BscArea): boolean {
    return this.plan?.idBscAreaSugerida != null && area.idBscArea === this.plan.idBscAreaSugerida;
  }

  tamano(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${FORMATO_NUM.format(bytes / (1024 * 1024))} MB`;
  }

  trackFila(_: number, f: FilaVista): number {
    return f.filaExcel;
  }

  trackMensaje(i: number, m: MensajeBsc): string {
    return `${i}:${m.codigo}`;
  }

  /* ============================ Utilerías ============================ */

  /** detectChanges y, si quedó pendiente, el foco en el primero de `foco` que exista. */
  private pinta(): void {
    this.cdr.detectChanges();
    const foco = this.foco;
    this.foco = [];
    for (const selector of foco) {
      const el = this.host.nativeElement.querySelector<HTMLElement>(selector);
      if (el) {
        el.focus();
        return;
      }
    }
  }

  /** El botón que reemplazó al que se pulsó en la fila (o su casilla). */
  private focoDeLaFila(fila: number): string[] {
    const tr = `tr[data-fila="${fila}"]`;
    return [`${tr} .carga-bsc__enlace`, `${tr} input[type="checkbox"]`];
  }

  /** questionCall con la guarda de la pantalla destruida. */
  private pregunta(texto: string, siConfirma: () => void): void {
    this.preguntaAbierta = true;
    this.msg.questionCall(texto, null, (r: { isConfirmed?: boolean }) => {
      this.preguntaAbierta = false;
      if (r?.isConfirmed && !this.destruido) {
        siConfirma();
      }
    });
  }

  /** "CODIGO Q2: mensaje" → { codigo, "Q2 · mensaje" }; "Fila 11: …" conserva la fila. */
  private parte(texto: string): MensajeBsc {
    const m = FORMATO_MENSAJE.exec(texto ?? "");
    if (!m) {
      return { codigo: "", texto: texto ?? "" };
    }
    const prefijo = [m[1], m[3]].filter((p) => !!p).join(" · ");
    return { codigo: m[2], texto: prefijo ? `${prefijo}: ${m[4]}` : m[4] };
  }

  private textoAsignaciones(asignaciones: Map<number, number>): string | null {
    const texto = Array.from(asignaciones.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([fila, idKpi]) => `${fila}:${idKpi}`)
      .join(",");
    return texto || null;
  }

  private formaDe(pedido: BscImportacionPedido): void {
    this.forma = {
      modo: pedido.modo,
      anio: pedido.anio ?? null,
      hastaTrimestre: pedido.hastaTrimestre ?? null,
      idBscArea: pedido.idBscArea ?? null,
      hoja: pedido.hoja ?? null,
    };
  }

  private armaAnios(): void {
    const anios: number[] = [];
    for (let a = this.anioEnCurso + ANIOS_ADELANTE; a >= this.anioEnCurso - ANIOS_ATRAS; a--) {
      anios.push(a);
    }
    // El del libro y el del enlace de Planeación, aunque caigan fuera del rango.
    for (const extra of [this.plan?.anio, this.forma.anio]) {
      if (extra != null && !anios.includes(extra)) {
        anios.push(extra);
      }
    }
    anios.sort((a, b) => b - a);
    this.aniosOpciones = anios;
  }

  /** "Q2", "Q2 y Q3", "Q1, Q2 y Q3". */
  private lista(partes: string[]): string {
    if (partes.length <= 1) {
      return partes.join("");
    }
    return `${partes.slice(0, -1).join(", ")} y ${partes.at(-1)}`;
  }

  private rango(desde: number, hasta: number): string {
    if (desde === hasta) {
      return `Q${desde}`;
    }
    return hasta === desde + 1 ? `Q${desde} y Q${hasta}` : `Q${desde} a Q${hasta}`;
  }

  /** Fracción 0..1 → porcentaje con a lo más 1 decimal, sin el signo (0.9 → "90"). */
  private pct(fraccion: number | null | undefined): string {
    if (fraccion == null) {
      return "—";
    }
    return FORMATO_PCT.format(Number(fraccion) * 100);
  }

  private mensaje(res: BscImportacionPlan | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || porOmision;
  }
}

/** En el resultado, lo que ya pasó con el bloque de cada trimestre. */
function notaDelResultado(nota: string): string {
  if (nota === NOTA_SE_CARGA) {
    return "avances cargados";
  }
  return nota === NOTA_SE_CONSERVA ? "se conservó" : nota;
}

/** AVANCES: qué pasa con el bloque de Qq del libro (después del corte, se conserva o se carga). */
function notaDeAvances(q: number, hasta: number, estatus: EstatusTrimestreBsc): string {
  if (q > hasta) {
    return NOTA_DESPUES_DEL_CORTE;
  }
  return TRIMESTRES_CERRADOS.has(estatus) ? NOTA_SE_CONSERVA : NOTA_SE_CARGA;
}
